import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { countryFlag } from '@/shared/lib/country'

/** App languages with the flag of their reference country. */
const LANGUAGES = [
  { code: 'en', label: 'English', country: 'GB' },
  { code: 'ro', label: 'Română', country: 'RO' },
  { code: 'es', label: 'Español', country: 'ES' },
  { code: 'bg', label: 'Български', country: 'BG' },
  { code: 'hr', label: 'Hrvatski', country: 'HR' },
  { code: 'cs', label: 'Čeština', country: 'CZ' },
  { code: 'da', label: 'Dansk', country: 'DK' },
  { code: 'nl', label: 'Nederlands', country: 'NL' },
  { code: 'et', label: 'Eesti', country: 'EE' },
  { code: 'fi', label: 'Suomi', country: 'FI' },
  { code: 'fr', label: 'Français', country: 'FR' },
  { code: 'de', label: 'Deutsch', country: 'DE' },
  { code: 'el', label: 'Ελληνικά', country: 'GR' },
  { code: 'hu', label: 'Magyar', country: 'HU' },
  { code: 'ga', label: 'Gaeilge', country: 'IE' },
  { code: 'it', label: 'Italiano', country: 'IT' },
  { code: 'lv', label: 'Latviešu', country: 'LV' },
  { code: 'lt', label: 'Lietuvių', country: 'LT' },
  { code: 'mt', label: 'Malti', country: 'MT' },
  { code: 'pl', label: 'Polski', country: 'PL' },
  { code: 'pt', label: 'Português', country: 'PT' },
  { code: 'sk', label: 'Slovenčina', country: 'SK' },
  { code: 'sl', label: 'Slovenščina', country: 'SI' },
  { code: 'sv', label: 'Svenska', country: 'SE' },
] as const

/**
 * Language dropdown with country flags. A custom listbox instead of a native
 * <select> because options can't render flags across platforms.
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ?? LANGUAGES[0]

  // Close on click outside or Escape.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const select = (code: string) => {
    setOpen(false)
    void i18n.changeLanguage(code)
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="Language"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="border-border bg-surface text-fg hover:bg-surface-hover flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors"
      >
        <span aria-hidden>{countryFlag(current.country)}</span>
        {current.label}
        <ChevronDown className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Language"
          className="border-border bg-surface absolute right-0 z-50 mt-1.5 max-h-72 w-44 overflow-y-auto rounded-xl border py-1 shadow-lg"
        >
          {LANGUAGES.map(({ code, label, country }) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={code === current.code}
                onClick={() => select(code)}
                className="hover:bg-surface-hover flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-semibold transition-colors"
              >
                <span aria-hidden className="text-sm">{countryFlag(country)}</span>
                <span className="text-fg flex-1">{label}</span>
                {code === current.code && <Check className="text-primary size-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
